import type { FilterInput } from '../constants';

export type ParsedI6Lookup = {
  filters: FilterInput[];
  logicalOperator: 'And' | 'Or';
};

/**
 * Parse the complete lookup predicate. Legacy i4 values are literal; only
 * lookups explicitly marked `json` use JSON string escaping. Never infer the
 * encoding from a backslash, which can be valid data in either format.
 *
 * Example:
 *   filterGroups: { filters: [{ field: SOCIETY_MEMBER_FIELD15, operator: CONTAINS, value: "2" }, ...] logicalOperator: OR }
 */
export function parseI6LookupString(
  raw?: string,
  valueEncoding?: string,
): ParsedI6Lookup {
  const invalid: ParsedI6Lookup = { filters: [], logicalOperator: 'And' };
  if (!raw || (valueEncoding !== undefined && valueEncoding !== 'json'))
    return invalid;

  const group = raw.match(
    /^\s*filterGroups\s*:\s*\{\s*filters\s*:\s*\[([\s\S]*)\]\s*,?\s*logicalOperator\s*:\s*(AND|OR)\s*\}\s*$/i,
  );
  if (!group) return invalid;

  // i4 also emits unquoted numbers. Null operators may omit their value.
  // Sticky matching and explicit separators prevent a malformed clause from
  // being skipped while valid clauses in the same predicate are retained.
  const rows = group[1]!.trim();
  const row =
    valueEncoding === 'json'
      ? /\s*\{\s*field:\s*(\w+)\s*,\s*operator:\s*(\w+)(?:\s*,\s*value:\s*("(?:\\.|[^"\\])*"|[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|null))?\s*\}\s*/iy
      : /\s*\{\s*field:\s*(\w+)\s*,\s*operator:\s*(\w+)(?:\s*,\s*value:\s*("[^"]*"|[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|null))?\s*\}\s*/iy;

  const filters: FilterInput[] = [];
  let offset = 0;
  while (offset < rows.length) {
    row.lastIndex = offset;
    const match = row.exec(rows);
    if (!match) return invalid;
    const [, field, operator, rawValue] = match;
    const nullOperator = /^(IS_NULL|NOT_NULL)$/i.test(operator!);
    let value = '';
    if (rawValue === undefined || /^null$/i.test(rawValue)) {
      if (!nullOperator) return invalid;
    } else if (rawValue.startsWith('"')) {
      try {
        value =
          valueEncoding === 'json'
            ? (JSON.parse(rawValue) as string)
            : rawValue.slice(1, -1);
      } catch {
        return invalid;
      }
    } else {
      value = rawValue;
    }
    filters.push({ field: field!, operator: operator!, value });
    offset = row.lastIndex;
    if (offset === rows.length) break;
    if (rows[offset] !== ',' || offset + 1 === rows.length) return invalid;
    offset += 1;
  }

  return {
    filters,
    logicalOperator: group[2]!.toUpperCase() === 'OR' ? 'Or' : 'And',
  };
}
